import React, { useEffect, useRef } from 'react';
import { motion } from "framer-motion";
import { Award, Briefcase, Users, BookCheck, Lightbulb, Layers } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

const WhyChooseUs = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const descRef = useRef(null);
  const buttonRef = useRef(null);
  const featuresRef = useRef([]);

  const features = [
    {
      icon: <Award className="w-10 h-10 text-[#0b9949]" />,
      title: "20+ Years of Expertise",
      desc: "Decades of proven experience delivering high-quality solutions across multiple industries.",
    },
    {
      icon: <Layers className="w-10 h-10 text-[#0b9949]" />,
      title: "Strong Industry Standards",
      desc: "We follow widely-recognized industry benchmarks to ensure consistent and reliable outcomes.",
    },
    {
      icon: <Briefcase className="w-10 h-10 text-[#0b9949]" />,
      title: "Trusted by Businesses",
      desc: "Organizations rely on us for our transparent processes, professional approach, and long-term value.",
    },
    {
      icon: <Users className="w-10 h-10 text-[#0b9949]" />,
      title: "Client-Focused Approach",
      desc: "We work closely with clients to ensure every solution matches their goals and expectations.",
    },
    {
      icon: <BookCheck className="w-10 h-10 text-[#0b9949]" />,
      title: "Skilled Professionals",
      desc: "Our team consists of highly knowledgeable and well-trained experts committed to excellence.",
    },
    {
      icon: <Lightbulb className="w-10 h-10 text-[#0b9949]" />,
      title: "Innovative Solutions",
      desc: "We bring fresh ideas, modern methodologies, and strategic thinking to every project.",
    },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(titleRef.current, { opacity: 0, y: 50 }, {
        opacity: 1, y: 0, duration: 1, ease: "power2.out",
        scrollTrigger: { trigger: titleRef.current, start: "top 80%" },
      });

      gsap.fromTo(descRef.current, { opacity: 0, y: 30 }, {
        opacity: 1, y: 0, duration: 1, delay: 0.2, ease: "power2.out",
        scrollTrigger: { trigger: descRef.current, start: "top 80%" },
      });

      gsap.fromTo(buttonRef.current, { opacity: 0, y: 20 }, {
        opacity: 1, y: 0, duration: 1, delay: 0.4, ease: "power2.out",
        scrollTrigger: { trigger: buttonRef.current, start: "top 80%" },
      });

      featuresRef.current.forEach((el, index) => {
        gsap.fromTo(el, { opacity: 0, y: 80, scale: 0.9 }, {
          opacity: 1, y: 0, scale: 1,
          duration: 1, delay: index * 0.15, ease: "power2.out",
          scrollTrigger: { trigger: el, start: "top 85%" },
        });
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 px-6 md:px-16 bg-[#f6fdf9] relative overflow-hidden">

      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1557683304-673a23048d34?auto=format&fit=crop&w=1400&q=60')] bg-cover bg-center opacity-5"></div>

      <div className="relative z-10 text-center mb-12">
        <motion.h2
          ref={titleRef}
          className="text-4xl font-bold text-[#0b9949] mb-4"
        >
          WHY CHOOSE US
        </motion.h2>

        <motion.p
          ref={descRef}
          className="text-gray-700 max-w-3xl mx-auto text-lg"
        >
          We combine experience, innovation, and a client-focused approach to deliver meaningful and reliable results.
        </motion.p>

        <button
          ref={buttonRef}
          onClick={() => navigate('/about')}
          className="bg-[#0b9949] text-white px-6 py-3 rounded-full hover:bg-[#097c3c] transition-all mt-4"
        >
          Read More
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 mt-10">
        {features.map((item, i) => (
          <motion.div
            key={i}
            ref={(el) => (featuresRef.current[i] = el)}
            className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 text-left border-t-4 border-[#0b9949]/50"
            whileHover={{ scale: 1.03 }}
          >
            <div className="mb-4">{item.icon}</div>
            <h3 className="text-xl font-semibold text-[#0b9949] mb-2">{item.title}</h3>
            <p className="text-gray-700 leading-relaxed">{item.desc}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default WhyChooseUs;
